/*
  As a user, I only want to toss either a collection or an entry and get all the relevant results.
  The purpose of this utility is to make access across EnhancedProducts and AppProducts as trivial as possible.
  The application (used to be or) is littered with `getAllGroups` and similar functions that all do the same subgroup searching and collecting.
  So far it's more useful to return all sub-entries together with the entries

  Overloads specifically split `T[]` and `T` variants to allow for better doc strings
*/
import type { EnhancedProductFamily, EnhancedProductGroup, EnhancedProductVariant } from '#api/enhancements/enhancedTmsApiTypes'
import type { AppProductFamily, AppProductGroup, AppProductVariant } from '#app/types/AppTypes'

/**
 * Gets all subgroups of a product group, including the product group as `[group, subGroup1, subGroup2, ...]`
 */
export function getAllGroups(group: EnhancedProductGroup | undefined): EnhancedProductGroup[]
/**
 * Gets all subgroups of a product group, including the product group as `[group, subGroup1, subGroup2, ...]`
 */
export function getAllGroups(group: AppProductGroup | undefined): AppProductGroup[]
/**
 * Gets all subGroups and group itself as `[groupA, subGroupA1, subGroupA2, groupB, subGroupB1, ...]`
 */
export function getAllGroups(groups: EnhancedProductGroup[] | undefined): EnhancedProductGroup[]
/**
 * Gets all subGroups and group itself as `[groupA, subGroupA1, subGroupA2, groupB, subGroupB1, ...]`
 */
export function getAllGroups(groups: AppProductGroup[] | undefined): AppProductGroup[]
export function getAllGroups(
  groups: AppProductGroup | AppProductGroup[] | EnhancedProductGroup | EnhancedProductGroup[] | undefined,
): (AppProductGroup | EnhancedProductGroup)[] {
  if (groups === undefined) {
    return []
  }
  if (Array.isArray(groups)) {
    return groups.flatMap((x) => getAllGroups(x))
  }
  return [groups, ...(groups.subGroups ?? []).flatMap((x) => getAllGroups(x))]
}

/**
 * Gets all subgroups of a product group, without the given product group as `[subGroup1, subGroup2, ...]`
 */
export function getOnlySubgroups(group: EnhancedProductGroup | undefined): EnhancedProductGroup[]
/**
 * Gets all subgroups of a product group, without the product group as `[subGroup1, subGroup2, ...]`
 */
export function getOnlySubgroups(group: AppProductGroup | undefined): AppProductGroup[]
/**
 * Gets all subGroups of all product groups as `[subGroupA1, subGroupA2, subGroupB1, ...]`
 */
export function getOnlySubgroups(groups: EnhancedProductGroup[] | undefined): EnhancedProductGroup[]
/**
 * Gets all subGroups of all product groups as `[subGroupA1, subGroupA2, subGroupB1, ...]`
 */
export function getOnlySubgroups(
  groups: AppProductGroup | AppProductGroup[] | EnhancedProductGroup | EnhancedProductGroup[] | undefined,
): (AppProductGroup | EnhancedProductGroup)[] {
  if (groups === undefined) {
    return []
  }
  if (Array.isArray(groups)) {
    return groups
      .map((x) => x.subGroups)
      .filter((x) => x !== undefined)
      .flatMap((x) => getAllGroups(x))
  }
  return (groups.subGroups ?? []).flatMap((x) => getAllGroups(x))
}

/** Gets all families in order of discovery from all groups passed: `[familyGroupA1, familyGroupA2, familyGroupB1, familyGroupB2, ...]` */
export function getAllFamilies(groups: EnhancedProductGroup[] | undefined): EnhancedProductFamily[]
export function getAllFamilies(groups: AppProductGroup[] | undefined): AppProductFamily[]
/** Gets all families of that group */
export function getAllFamilies(group: EnhancedProductGroup | undefined): EnhancedProductFamily[]
/** Gets all families of that group */
export function getAllFamilies(group: AppProductGroup | undefined): AppProductFamily[]
export function getAllFamilies(
  groups: AppProductGroup | AppProductGroup[] | EnhancedProductGroup | EnhancedProductGroup[] | undefined,
): AppProductFamily[] | EnhancedProductFamily[] {
  if (groups === undefined) {
    return []
  }
  if (Array.isArray(groups)) {
    return getAllGroups(groups).flatMap((x) => getAllFamilies(x))
  }
  return groups.families ?? []
}

const isAppType = (
  entry: AppProductGroup | AppProductFamily | EnhancedProductGroup | EnhancedProductFamily,
): entry is AppProductGroup | AppProductFamily => 'enrichment' in entry
const isProductFamily = (entry: EnhancedProductGroup | EnhancedProductFamily): entry is EnhancedProductFamily => 'variants' in entry
const isAppFamily = (entry: AppProductGroup | AppProductFamily): entry is AppProductFamily => 'variants' in entry
const isArrayOfGroups = (
  array: AppProductGroup[] | EnhancedProductGroup[] | AppProductFamily[] | EnhancedProductFamily[],
): array is AppProductGroup[] | EnhancedProductGroup[] => array.length > 1 && !('variants' in array[0])

/** Gets all variants in order of groups passed: `[variant1FamilyAGroupX, variant2FamilyAGroupX, variant1FamilyBGroupX, variant2FamilyBGroupX, variant1FamilyAGroupY, ...]` */
export function getAllVariants(groups: AppProductGroup[] | AppProductGroup | undefined): AppProductVariant[]
/** Gets all variants in order of groups passed: `[variant1FamilyAGroupX, variant2FamilyAGroupX, variant1FamilyBGroupX, variant2FamilyBGroupX, variant1FamilyAGroupY, ...]` */
export function getAllVariants(groups: EnhancedProductGroup[] | EnhancedProductGroup | undefined): EnhancedProductVariant[]
/** Gets all variants in order of discovery from all families passed: `[variantFamilyA1, variantFamilyA2, variantFamilyB1, variantFamilyB2, ...]` */
export function getAllVariants(families: AppProductFamily[] | undefined): AppProductVariant[]
export function getAllVariants(families: EnhancedProductFamily[] | undefined): EnhancedProductVariant[]
export function getAllVariants(family: AppProductFamily | undefined): AppProductVariant[]
export function getAllVariants(family: EnhancedProductFamily | undefined): EnhancedProductVariant[]
export function getAllVariants(
  familiesOrGroups:
    | AppProductFamily
    | AppProductFamily[]
    | EnhancedProductFamily
    | EnhancedProductFamily[]
    | AppProductGroup
    | AppProductGroup[]
    | EnhancedProductGroup
    | EnhancedProductGroup[]
    | undefined,
): AppProductVariant[] | EnhancedProductVariant[] {
  if (familiesOrGroups === undefined) {
    return []
  }
  if (Array.isArray(familiesOrGroups)) {
    // empty collections can return early, then it's easier to probe what's in the union
    if (familiesOrGroups.length <= 0) {
      return []
    }
    if (isArrayOfGroups(familiesOrGroups)) {
      return getAllVariants(getAllFamilies(familiesOrGroups))
    }
    return familiesOrGroups.flatMap((x) => getAllVariants(x))
  }

  // is it an AppProductGroup?
  if (isAppType(familiesOrGroups) && !isAppFamily(familiesOrGroups)) {
    return getAllVariants(getAllFamilies(familiesOrGroups))
  }
  // is it an EnhancedProductGroup?
  if (!isAppType(familiesOrGroups) && !isProductFamily(familiesOrGroups)) {
    return getAllVariants(getAllFamilies(familiesOrGroups))
  }
  // otherwise it's AppProductFamily | EnhancedProductFamily
  return familiesOrGroups.variants
}
